/**
 * RecommendationRegistry — recommendations from closed audits.
 * Props: organizationId, userId, userRole
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, RefreshCw, CheckCircle, Clock, User } from 'lucide-react';
import { recommendationService } from '../../services/recommendationService';
import Modal from '../ui/Modal';
import './RecommendationRegistry.css';

const STATUS_OPTIONS = [
  { value: 'all', labelKey: 'rec.filterAll' },
  { value: 'open', labelKey: 'rec.statusOpen' },
  { value: 'implemented', labelKey: 'rec.statusImplemented' }
];

const STATUS_BADGE = {
  open: 'badge-gray',
  acknowledged: 'badge-amber',
  implemented: 'badge-teal',
  closed: 'badge-blue'
};

const STATUS_ICON = {
  open: null,
  acknowledged: Clock,
  implemented: CheckCircle,
  closed: CheckCircle
};

const RecommendationRegistry = ({ organizationId, userId, userRole = 'administration' }) => {
  const { t } = useTranslation('recommendations');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({ status: 'all', searchText: '' });
  const [assignModal, setAssignModal] = useState(null);
  const [assignForm, setAssignForm] = useState({ assignedToName: '', assignedToGroup: '' });
  const [assigning, setAssigning] = useState(false);

  const canAct = ['management', 'ic_personnel'].includes(userRole);

  const fetchItems = useCallback(async () => {
    if (!organizationId) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const list = await recommendationService.getByOrganization(organizationId, {});
      setItems(list);
    } catch (err) {
      setError(err.message || t('rec.errorLoading'));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [organizationId, t]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const filtered = useMemo(() => {
    const st = filters.status;
    const q = (filters.searchText || '').trim().toLowerCase();
    return items.filter((item) => {
      if (st && st !== 'all' && item.status !== st) return false;
      if (q && !(item.description || '').toLowerCase().includes(q)) return false;
      return true;
    });
  }, [items, filters.status, filters.searchText]);

  const handleAssignOwner = async () => {
    if (!assignModal || !canAct) return;
    setAssigning(true);
    try {
      await recommendationService.assignOwnerAndGenerateTask(
        assignModal.recId,
        {
          assignedTo: null,
          assignedToName: assignForm.assignedToName,
          assignedToGroup: assignForm.assignedToGroup || null,
          assignedToGroupName: assignForm.assignedToGroup || ''
        },
        userId
      );
      setAssignModal(null);
      setAssignForm({ assignedToName: '', assignedToGroup: '' });
      fetchItems();
    } catch (err) {
      console.error('[RecommendationRegistry] assign failed:', err);
    } finally {
      setAssigning(false);
    }
  };

  const formatDate = (ts) => {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('ro-RO');
  };

  const statusLabel = (s) => {
    const map = {
      open: t('rec.statusOpen'),
      acknowledged: t('rec.statusAcknowledged'),
      implemented: t('rec.statusImplemented'),
      closed: t('rec.statusClosed')
    };
    return map[s] || s;
  };

  if (!organizationId) {
    return (
      <div className="rec-registry">
        <p className="rec-empty">{t('rec.noOrganization')}</p>
      </div>
    );
  }

  return (
    <div className="rec-registry">
      <div className="rec-header">
        <h2>{t('rec.title')}</h2>
        <button type="button" className="btn-icon" onClick={fetchItems} title={t('rec.refresh')}>
          <RefreshCw size={16} />
        </button>
      </div>

      <div className="rec-filters">
        <select
          value={filters.status}
          onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
        >
          {STATUS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {t(opt.labelKey)}
            </option>
          ))}
        </select>
        <input
          type="search"
          className="rec-search"
          placeholder={t('rec.searchPlaceholder')}
          value={filters.searchText}
          onChange={(e) => setFilters((f) => ({ ...f, searchText: e.target.value }))}
        />
      </div>

      {error && (
        <div className="rec-error">
          <AlertTriangle size={16} /> {error}
        </div>
      )}
      {loading && <div className="rec-loading">{t('rec.loading')}</div>}
      {!loading && filtered.length === 0 && <p className="rec-empty">{t('rec.noItems')}</p>}

      <div className="rec-items">
        {filtered.map((rec) => {
          const StatusIcon = STATUS_ICON[rec.status];
          const hasNoTask = !rec.taskInstanceId;

          return (
            <div key={rec.id} className={`rec-card ${hasNoTask ? 'rec-card-no-task' : ''}`}>
              <div className="rec-card-header">
                <span className="rec-code">{rec.recommendation_code}</span>
                <span className={`rec-status-badge ${STATUS_BADGE[rec.status] || 'badge-gray'}`}>
                  {StatusIcon && <StatusIcon size={12} />}
                  {statusLabel(rec.status)}
                </span>
              </div>

              <div className="rec-audit-context">
                <span className="rec-audit-ref">{rec.auditTitle || rec.auditId}</span>
                {rec.auditDate && <span className="rec-audit-date">{formatDate(rec.auditDate)}</span>}
                {rec.auditorName && <span className="rec-auditor">· {rec.auditorName}</span>}
              </div>

              <p className="rec-description">{rec.description}</p>

              {rec.auditCorrectiveActions?.length > 0 && (
                <div className="rec-corrective-context">
                  <span className="rec-corrective-label">{t('rec.correctiveActionsLabel')}:</span>
                  {rec.auditCorrectiveActions.map((ca) => (
                    <div key={ca.actionId} className="rec-corrective-item">
                      {ca.capaId ? (
                        <CheckCircle size={12} className="icon-done" />
                      ) : (
                        <Clock size={12} className="icon-pending" />
                      )}
                      <span>{ca.description}</span>
                      {ca.capaId && <span className="capa-ref">{ca.capaId.slice(0, 8)}…</span>}
                    </div>
                  ))}
                </div>
              )}

              <div className="rec-meta">
                {rec.assignedToName && (
                  <span className="rec-owner">
                    <User size={12} /> {rec.assignedToName}
                  </span>
                )}
                {rec.dueDate && (
                  <span className="rec-due">
                    {t('rec.due')}: {formatDate(rec.dueDate)}
                  </span>
                )}
                {rec.taskInstanceId && (
                  <span className="rec-task-ref">Task: {rec.taskInstanceId.slice(0, 8)}…</span>
                )}
              </div>

              {hasNoTask && canAct && (
                <div className="rec-no-task-banner">
                  <AlertTriangle size={14} />
                  <span>{t('rec.noTaskBanner')}</span>
                  <button
                    type="button"
                    className="btn-secondary btn-sm"
                    onClick={() => {
                      setAssignModal({ recId: rec.id, rec });
                      setAssignForm({ assignedToName: '', assignedToGroup: '' });
                    }}
                  >
                    {t('rec.assignOwner')}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {assignModal && (
        <Modal isOpen onClose={() => setAssignModal(null)} title={t('rec.assignOwnerTitle')} size="medium">
          <div className="rec-assign-modal">
            <p className="rec-assign-desc">{assignModal.rec.description}</p>
            <div className="form-row">
              <label>{t('rec.ownerName')}</label>
              <input
                type="text"
                value={assignForm.assignedToName}
                onChange={(e) => setAssignForm((prev) => ({ ...prev, assignedToName: e.target.value }))}
                placeholder={t('rec.ownerNamePlaceholder')}
                className="form-input"
              />
            </div>
            <div className="form-row">
              <label>{t('rec.groupOptional')}</label>
              <input
                type="text"
                value={assignForm.assignedToGroup}
                onChange={(e) => setAssignForm((prev) => ({ ...prev, assignedToGroup: e.target.value }))}
                placeholder={t('rec.groupPlaceholder')}
                className="form-input"
              />
            </div>
            <div className="modal-actions">
              <button type="button" onClick={() => setAssignModal(null)} disabled={assigning}>
                {t('rec.cancel')}
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={handleAssignOwner}
                disabled={assigning || !assignForm.assignedToName.trim()}
              >
                {assigning ? t('rec.saving') : t('rec.assignAndGenerateTask')}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default RecommendationRegistry;
